import { Typography, Modal, IconNode, Button } from '@components/base';
import { TextField, InputDatePicker, SelectLabel, DragDropUpload } from '@components/common';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import Images from '@assets/images';
import { addBLDetails } from '@services/task.service';
import notify from '@helpers/toastify-helper';
import moment from 'moment';
import css from './index.module.scss';
import { useState } from 'react';
import { BL_TYPES, TRANSPORTATION_MODE_ENUM } from '@helpers/constants';
import { getDiffBetweenTwoDates, getFormattedDate } from '@helpers/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  taskData: any;
  onComplete?: () => void;
  taskName: 'BL';
  transportation_mode: 'SEA' | 'RAIL' | 'ROAD';
  sendStatusToCard: (data: boolean) => void;
}

const BLDetailsModal = ({
  open,
  onClose,
  taskData,
  onComplete,
  taskName,
  transportation_mode,
  sendStatusToCard
}: any) => {
  const { task_id, export_clearance_obtained_actual_date } = taskData;

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isSubmitting }
  } = useFormContext();

  const onFormSubmit = async (data: any) => {
    const dateDiff = getDiffBetweenTwoDates(data.bl_date, export_clearance_obtained_actual_date);
    if (dateDiff < 0) {
      setError('bl_date', {
        message:
          'BL Date cannot be before Export Clearance Obtained Date, contact Tech Team to back‑date'
      });
      return;
    }

    const base = {
      bl_date: moment(data.bl_date).format('YYYY-MM-DD'),
      bl_number: data.bl_number
    };

    let body: any = {};
    if (transportation_mode === TRANSPORTATION_MODE_ENUM.SEA) {
      body = {
        ...base,
        bl_type: data.bl_type?.value
      };
    } else {
      const fd = new FormData();
      Object.entries(base).forEach(([k, v]) => fd.append(k, v as string));
      fd.append('bl_document', data.bl_document);
      body = fd;
    }

    const response = await addBLDetails(task_id, body);
    if (response.success) {
      notify({ message: 'BL details added successfully' });
      onComplete?.();
      sendStatusToCard?.();
      onClose();
    } else {
      notify({
        message: response.error ?? 'Something went wrong!',
        severity: 'error'
      });
    }
  };

  const watch = useWatch({
    control,
    name: 'bl_document'
  });

  const toggleDatePicker = () => setIsDatePickerOpen((prev) => !prev);

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      setValue('bl_document', files[0]);
    }
  };
  const handleDeleteFile = () => {
    setValue('bl_document', undefined);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={`${css.modalWrapper} ${isDatePickerOpen ? css.modalWrapperOpen : ''}`}>
        <section className={css.modalHeader}>
          <Typography variant="h4">Add {taskName} Details</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>

        <form noValidate className={css.formWrapper} onSubmit={handleSubmit(onFormSubmit)}>
          <section className={css.modalContent}>
            {transportation_mode === TRANSPORTATION_MODE_ENUM.SEA ? (
              <Controller
                name="bl_type"
                control={control}
                render={({ field, fieldState }) => (
                  <SelectLabel
                    {...field}
                    required
                    isSearchable
                    label="BL Type"
                    options={BL_TYPES}
                    placeholder="Select one"
                    getOptionLabel={(o: any) => o.label}
                    getOptionValue={(o: any) => o.value}
                    rootClassName={css.blTypeFieldWrapper}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            ) : (
              <label htmlFor="Payment">
                <Controller
                  name="bl_document"
                  control={control}
                  render={({ fieldState }) => (
                    <DragDropUpload
                      onDrop={handleFileSelect}
                      required
                      label="Upload Proof"
                      error={fieldState.invalid}
                      rootClassName={css.fileUploadWrapper}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                {watch && (
                  <div className={css.fileUploadState}>
                    <Typography variant="p" className={css.fileName}>
                      {watch.name}
                    </Typography>
                    <IconNode
                      src={Images.deleteRed}
                      alt="delete icon"
                      component="button"
                      className={css.deleteButton}
                      onClick={handleDeleteFile}
                    />
                  </div>
                )}
              </label>
            )}
            <Controller
              name="bl_date"
              control={control}
              render={({ field, fieldState }) => (
                <InputDatePicker
                  {...field}
                  required
                  setDatePickerOpen={setIsDatePickerOpen}
                  value={field.value}
                  placeholder="Select date"
                  label="BL Date"
                  onSelect={(day: Date | undefined) => {
                    field.onChange(day ?? null);
                    toggleDatePicker();
                  }}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  rootClassName={css.fieldSpacing}
                />
              )}
            />
            <Controller
              name="bl_number"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  required
                  enterKeyHint="next"
                  label="BL Number"
                  placeholder="Enter BL number"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  rootClassName={isDatePickerOpen ? css.fieldWrapperOpenModal : css.fieldWrapper}
                />
              )}
            />
          </section>
          <section className={css.modalFooter}>
            <Button variant="outlined-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </section>
        </form>
      </div>
    </Modal>
  );
};

export default BLDetailsModal;
